import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { AUTH_IDS, NAV } from "@/constants/testIds";
import { formatApiErrorDetail } from "@/lib/api";
import { Ticket } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      const from = loc.state?.from || "/dashboard";
      nav(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-zinc-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-[0.06]" aria-hidden="true" />
        <Link to="/" className="flex items-center gap-2 font-display font-black text-xl relative" data-testid={NAV.logo}>
          <span className="w-8 h-8 grid place-items-center bg-white text-zinc-900 rounded-md">
            <Ticket className="w-4 h-4" />
          </span>
          <span className="tracking-tight">Affra<span className="text-indigo-400">.</span></span>
        </Link>
        <div className="relative">
          <div className="font-display font-black text-5xl leading-tight tracking-tighter">
            Sell tickets. Under your brand.
          </div>
          <p className="mt-4 text-zinc-400 max-w-md">
            You always keep 100% of your ticket price. We handle the rest.
          </p>
        </div>
        <div className="text-xs text-zinc-500 relative">© {new Date().getFullYear()} Affra</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex mb-8 lg:hidden" data-testid={`${NAV.logo}-mobile`}>
            <Logo size="h-20" />
          </Link>
          <Link to="/" className="hidden lg:inline-flex mb-8" data-testid={`${NAV.logo}-desktop`}>
            <Logo size="h-24" />
          </Link>
          <div className="mb-8">
            <h1 className="font-display font-black text-3xl tracking-tight">Log in</h1>
            <p className="mt-2 text-zinc-500 text-sm">Welcome back. Access your dashboard.</p>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid={AUTH_IDS.loginForm}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid={AUTH_IDS.loginEmail}
                className="mt-2 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid={AUTH_IDS.loginPassword}
                className="mt-2 h-11"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600" data-testid={AUTH_IDS.error}>{error}</div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid={AUTH_IDS.loginSubmit}
              className="w-full h-11 rounded-full bg-zinc-900 hover:bg-zinc-800"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="mt-6 text-sm text-zinc-500">
            No account yet?{" "}
            <Link to="/register" className="text-indigo-600 font-medium hover:underline">Create one</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
